import { useEffect, useRef } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { liveSocket, type WsEvent } from "@/lib/live";
import { pushToast } from "@/lib/toast";

/** The full set of top-level query roots a reconnect must assume stale. */
const ALL_ROOTS = ["signals", "signal", "orders", "positions", "account", "decisions", "channels", "settings", "health", "notifications", "selfTrader"];

/**
 * One frame in, the right invalidation out. Exported standalone (not inlined in the hook)
 * so the topic → action map is testable without mounting React or opening a socket.
 */
export function applyLiveEvent(queryClient: QueryClient, event: WsEvent): void {
  const topic = event.topic;

  if (topic.startsWith("quote:")) return; // handled by lib/live.ts's own quote store

  switch (topic) {
    case "signal": {
      void queryClient.invalidateQueries({ queryKey: ["signals"] });
      const payload = event.payload as { id?: number } | undefined;
      if (typeof payload?.id === "number") {
        void queryClient.invalidateQueries({ queryKey: ["signal", payload.id] });
      }
      return;
    }
    case "order":
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["positions"] });
      return;
    case "position":
      void queryClient.invalidateQueries({ queryKey: ["positions"] });
      return;
    case "account": {
      const payload = event.payload as Record<string, unknown>;
      queryClient.setQueryData(["account"], (old: unknown) => {
        const prevAccount =
          old && typeof old === "object" && "account" in old && old.account && typeof old.account === "object"
            ? (old.account as Record<string, unknown>)
            : {};
        return { live: true, account: { ...prevAccount, ...payload } };
      });
      return;
    }
    case "notification": {
      // plain is the server's text rendering of the telegram-html body; the body
      // fallback only matters mid-deploy against a server that predates it.
      const payload = event.payload as { body?: string; plain?: string } | undefined;
      pushToast(payload?.plain ?? payload?.body ?? "New notification");
      // M23 — the toast is transient and the badge is not. Without this the count only moves
      // on the next poll, so a notification arriving while the app is open reads as already
      // seen.
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      return;
    }
    case "health":
      void queryClient.invalidateQueries({ queryKey: ["health"] });
      return;
    default:
      // Unknown topics are ignored, not thrown — a server ahead of this client's build
      // must not crash the app over a topic it doesn't understand yet.
      return;
  }
}

/** A dropped frame may delay information, never lose it — so a reconnect assumes everything
 *  might have changed and refetches the world rather than trying to resume mid-stream. */
export function invalidateAll(queryClient: QueryClient): void {
  for (const root of ALL_ROOTS) void queryClient.invalidateQueries({ queryKey: [root] });
}

/** Owns the one socket connection for the app's lifetime. Mount once, at the app shell. */
export function useLiveEvents(enabled: boolean): void {
  const queryClient = useQueryClient();
  const connectedBefore = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const unsubscribeEvents = liveSocket.subscribe((event) => applyLiveEvent(queryClient, event));
    const unsubscribeConnection = liveSocket.onConnectionChange((state) => {
      if (state === "open") {
        if (connectedBefore.current) invalidateAll(queryClient);
        connectedBefore.current = true;
      }
    });

    liveSocket.connect();

    return () => {
      unsubscribeEvents();
      unsubscribeConnection();
      liveSocket.disconnect();
      connectedBefore.current = false;
    };
  }, [enabled, queryClient]);
}
